import { put, call, takeLatest, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import { INVESTOR_TRACKER } from '../../../components/global/Server/endpoints';
import { hideError, showErrorMessage } from '../../actions/global';
import {
    LIST_INVESTOR_TRACKER,
    SAVE_LOCATION,
    DELETE_LOCATION,
    SAVE_PERSON,
    DELETE_PERSON,
    SAVE_EXPENSE,
    DELETE_EXPENSE,
    SAVE_PAYMENT,
    UPDATE_PAYMENT_STATUS,
    UPDATE_PARTICIPATION,
    SAVE_CONTRACTOR,
    SAVE_QUOTE,
    DELETE_QUOTE,
    SAVE_CONTRACTOR_PAYMENT,
    DELETE_CONTRACTOR_PAYMENT,
    SAVE_WEEKLY_PAYMENT,
} from '../../constants/investorTracker';
import {
    storeInvestorTracker,
    storeLocation,
    removeLocation,
    storePerson,
    removePerson,
} from '../../actions/investorTracker';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../components/dashboard/constants';

// LIST — pulls companies, people, and locations the user can see in one shot.
function* listInvestorTrackerSaga(action) {
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(INVESTOR_TRACKER.LIST_ALL, action.data || {}),
        );

        if (response.status === 200 && response.data.success) {
            yield put(
                storeInvestorTracker({
                    companies: response.data.companies || [],
                    people: response.data.people || [],
                    locations: response.data.locations || [],
                    currentPersonId: response.data.currentPersonId || null,
                }),
            );
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    }
}

// All write endpoints follow the same contract: POST returns
// { success, location } where `location` is the full updated entity.
// The reducer upserts it. Server-side controllers should re-derive
// participations / nested arrays from the location document so the
// client always gets a consistent snapshot.
function* writeAndStoreLocation(endpoint, action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(endpoint, action.data),
        );

        if (response.status === 200 && response.data.success) {
            if (response.data.location) {
                yield put(storeLocation(response.data.location));
            }
            if (typeof action.callback === 'function') {
                action.callback(null, response.data.message);
            }
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') {
                action.callback(message, null);
            }
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') {
            action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
        }
    }
}

function* saveLocationSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_LOCATION, action); }
function* saveExpenseSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_EXPENSE, action); }
function* deleteExpenseSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.DELETE_EXPENSE, action); }
function* savePaymentSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_PAYMENT, action); }
function* updatePaymentStatusSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.UPDATE_PAYMENT_STATUS, action); }
function* updateParticipationSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.UPDATE_PARTICIPATION, action); }
function* saveContractorSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_CONTRACTOR, action); }
function* saveQuoteSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_QUOTE, action); }
function* deleteQuoteSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.DELETE_QUOTE, action); }
function* saveContractorPaymentSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_CONTRACTOR_PAYMENT, action); }
function* deleteContractorPaymentSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.DELETE_CONTRACTOR_PAYMENT, action); }
function* saveWeeklyPaymentSaga(action) { yield* writeAndStoreLocation(INVESTOR_TRACKER.SAVE_WEEKLY_PAYMENT, action); }

// DELETE LOCATION — removes from state instead of upserting.
function* deleteLocationSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(INVESTOR_TRACKER.DELETE_LOCATION, action.data),
        );

        if (response.status === 200 && response.data.success) {
            yield put(removeLocation(response.data.locationId || action.data.locationId));
            if (typeof action.callback === 'function') {
                action.callback(null, response.data.message);
            }
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') {
                action.callback(message, null);
            }
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') {
            action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
        }
    }
}

// SAVE PERSON — server returns { success, person }; reducer upserts.
function* savePersonSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(INVESTOR_TRACKER.SAVE_PERSON, action.data),
        );
        if (response.status === 200 && response.data.success) {
            if (response.data.person) yield put(storePerson(response.data.person));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

// DELETE PERSON — server returns { success, personId }.
function* deletePersonSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(INVESTOR_TRACKER.DELETE_PERSON, action.data),
        );
        if (response.status === 200 && response.data.success) {
            yield put(removePerson(response.data.personId || action.data.personId));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

export default function* investorTrackerSagas() {
    yield takeLatest(LIST_INVESTOR_TRACKER, listInvestorTrackerSaga);
    yield takeEvery(SAVE_LOCATION, saveLocationSaga);
    yield takeEvery(DELETE_LOCATION, deleteLocationSaga);
    yield takeEvery(SAVE_PERSON, savePersonSaga);
    yield takeEvery(DELETE_PERSON, deletePersonSaga);
    yield takeEvery(SAVE_EXPENSE, saveExpenseSaga);
    yield takeEvery(DELETE_EXPENSE, deleteExpenseSaga);
    yield takeEvery(SAVE_PAYMENT, savePaymentSaga);
    yield takeEvery(UPDATE_PAYMENT_STATUS, updatePaymentStatusSaga);
    yield takeEvery(UPDATE_PARTICIPATION, updateParticipationSaga);
    yield takeEvery(SAVE_CONTRACTOR, saveContractorSaga);
    yield takeEvery(SAVE_QUOTE, saveQuoteSaga);
    yield takeEvery(DELETE_QUOTE, deleteQuoteSaga);
    yield takeEvery(SAVE_CONTRACTOR_PAYMENT, saveContractorPaymentSaga);
    yield takeEvery(DELETE_CONTRACTOR_PAYMENT, deleteContractorPaymentSaga);
    yield takeEvery(SAVE_WEEKLY_PAYMENT, saveWeeklyPaymentSaga);
}
